import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { loginWithCredentials, logoutCurrentSession } from '@/api/auth/authApi';
import type { AuthenticationTokens } from '@/api/auth/authApi.types';
import { fetchCurrentContext } from '@/api/context/currentContextApi';
import { fetchCurrentUser } from '@/api/users/usersApi';
import type { Membership } from '@/api/context/currentContextApi.types';
import type { PlatformUser } from '@/api/users/usersApi.types';
import { refreshAuthenticationSession } from '@/api/tokenRefresh';
import {
  clearActiveWorkspaceSelection,
  clearAuthenticationTokens,
  hasStoredAccessToken,
  readStoredAccessToken,
  SESSION_EXPIRED_EVENT_NAME,
  storeActiveWorkspaceSelection,
  storeAuthenticationTokens,
  UNAUTHORIZED_EVENT_NAME,
} from '@/lib/authStorage';
import { isTokenExpiringWithin } from '@/lib/jwtExpiry';
import { applyAccountLanguage } from '@/lib/applyAccountLanguage';
import { PLATFORM_WORKSPACE_SELECTION } from '@/constants/scopes';
import { PROACTIVE_TOKEN_REFRESH_THRESHOLD_MILLISECONDS } from '@/constants/session';
import {
  AuthenticationContext,
  type AuthenticationContextValue,
  type AuthenticationStatus,
  type WorkspaceSwitchTarget,
} from './authContext';

interface AuthProviderProps {
  children: ReactNode;
}

function findActiveMembership(
  memberships: Membership[],
  activeTenantId: string | null,
  activeScopeCode: string | null
): Membership | null {
  if (activeScopeCode === null) {
    return null;
  }
  if (activeScopeCode === 'platform') {
    const platformMembership = memberships.find(function isPlatform(membership) {
      return membership.scopeCode === 'platform';
    });
    return platformMembership === undefined ? null : platformMembership;
  }
  const matchingMembership = memberships.find(function matches(membership) {
    return membership.scopeCode === activeScopeCode && membership.tenantId === activeTenantId;
  });
  return matchingMembership === undefined ? null : matchingMembership;
}

function workspaceSelectionFor(target: WorkspaceSwitchTarget): string {
  if (target.scopeCode === 'platform') {
    return PLATFORM_WORKSPACE_SELECTION;
  }
  if (target.tenantId === null) {
    return '';
  }
  return target.scopeCode + ':' + target.tenantId;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthenticationStatus>('loading');
  const [currentUser, setCurrentUser] = useState<PlatformUser | null>(null);
  const [platformPermissions, setPlatformPermissions] = useState<string[]>([]);
  const [tenantPermissions, setTenantPermissions] = useState<string[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [activeScopeCode, setActiveScopeCode] = useState<string | null>(null);
  const [hasSessionExpiredNotice, setHasSessionExpiredNotice] = useState(false);

  const resetToUnauthenticated = useCallback(function resetSession() {
    setCurrentUser(null);
    setPlatformPermissions([]);
    setTenantPermissions([]);
    setMemberships([]);
    setEnabledModules([]);
    setActiveTenantId(null);
    setActiveScopeCode(null);
    setStatus('unauthenticated');
  }, []);

  const refreshCurrentUser = useCallback(async function reloadCurrentUser() {
    const user = await fetchCurrentUser();
    setCurrentUser(user);
  }, []);

  const loadAuthenticatedSession = useCallback(async function loadSession(): Promise<PlatformUser> {
    const context = await fetchCurrentContext();
    setCurrentUser(context.user);
    setPlatformPermissions(context.platformPermissions);
    setTenantPermissions(context.tenantPermissions);
    setMemberships(context.memberships);
    setEnabledModules(context.enabledModules);
    setActiveTenantId(context.activeTenantId);
    setActiveScopeCode(context.activeScopeCode);
    setStatus('authenticated');
    return context.user;
  }, []);

  useEffect(
    function restoreSessionOnStartup() {
      let isActive = true;
      async function restoreSession(): Promise<void> {
        if (hasStoredAccessToken() === false) {
          if (isActive === true) {
            resetToUnauthenticated();
          }
          return;
        }
        try {
          await loadAuthenticatedSession();
        } catch {
          clearAuthenticationTokens();
          clearActiveWorkspaceSelection();
          if (isActive === true) {
            resetToUnauthenticated();
          }
        }
      }
      restoreSession();
      return function stopRestoring() {
        isActive = false;
      };
    },
    [loadAuthenticatedSession, resetToUnauthenticated]
  );

  useEffect(
    function reactToUnauthorizedResponses() {
      function handleUnauthorized(): void {
        clearActiveWorkspaceSelection();
        resetToUnauthenticated();
      }
      window.addEventListener(UNAUTHORIZED_EVENT_NAME, handleUnauthorized);
      return function removeUnauthorizedListener() {
        window.removeEventListener(UNAUTHORIZED_EVENT_NAME, handleUnauthorized);
      };
    },
    [resetToUnauthenticated]
  );

  useEffect(
    function reactToSessionExpiration() {
      function handleSessionExpired(): void {
        clearActiveWorkspaceSelection();
        setHasSessionExpiredNotice(true);
        resetToUnauthenticated();
      }
      window.addEventListener(SESSION_EXPIRED_EVENT_NAME, handleSessionExpired);
      return function removeSessionExpiredListener() {
        window.removeEventListener(SESSION_EXPIRED_EVENT_NAME, handleSessionExpired);
      };
    },
    [resetToUnauthenticated]
  );

  useEffect(
    function refreshSessionProactivelyWhenTabRegainsFocus() {
      function refreshSessionIfAccessTokenIsNearExpiry(): void {
        if (status !== 'authenticated') {
          return;
        }
        const storedAccessToken = readStoredAccessToken();
        if (
          isTokenExpiringWithin(
            storedAccessToken,
            PROACTIVE_TOKEN_REFRESH_THRESHOLD_MILLISECONDS
          ) === false
        ) {
          return;
        }
        refreshAuthenticationSession().catch(function ignoreProactiveRefreshFailure() {
          return undefined;
        });
      }
      window.addEventListener('focus', refreshSessionIfAccessTokenIsNearExpiry);
      document.addEventListener('visibilitychange', refreshSessionIfAccessTokenIsNearExpiry);
      return function removeFocusListeners() {
        window.removeEventListener('focus', refreshSessionIfAccessTokenIsNearExpiry);
        document.removeEventListener(
          'visibilitychange',
          refreshSessionIfAccessTokenIsNearExpiry
        );
      };
    },
    [status]
  );

  const acknowledgeSessionExpiredNotice = useCallback(function acknowledgeNotice() {
    setHasSessionExpiredNotice(false);
  }, []);

  const logInWithCredentials = useCallback(
    async function logIn(email: string, password: string): Promise<void> {
      const tokens = await loginWithCredentials(email, password);
      clearActiveWorkspaceSelection();
      storeAuthenticationTokens(tokens.accessToken, tokens.refreshToken);
      const user = await loadAuthenticatedSession();
      await applyAccountLanguage(user.preferredLanguage);
    },
    [loadAuthenticatedSession]
  );

  const completeSessionWithTokens = useCallback(
    async function completeSession(tokens: AuthenticationTokens): Promise<void> {
      clearActiveWorkspaceSelection();
      storeAuthenticationTokens(tokens.accessToken, tokens.refreshToken);
      const user = await loadAuthenticatedSession();
      await applyAccountLanguage(user.preferredLanguage);
    },
    [loadAuthenticatedSession]
  );

  const switchWorkspace = useCallback(
    async function performSwitch(target: WorkspaceSwitchTarget): Promise<void> {
      storeActiveWorkspaceSelection(workspaceSelectionFor(target));
      queryClient.clear();
      await loadAuthenticatedSession();
    },
    [loadAuthenticatedSession, queryClient]
  );

  const logOut = useCallback(
    async function performLogout(): Promise<void> {
      try {
        await logoutCurrentSession();
      } catch (logoutError) {
        void logoutError;
      }
      clearAuthenticationTokens();
      clearActiveWorkspaceSelection();
      queryClient.clear();
      resetToUnauthenticated();
    },
    [queryClient, resetToUnauthenticated]
  );

  const activeMembership = findActiveMembership(memberships, activeTenantId, activeScopeCode);
  const activeWorkspace = activeScopeCode === 'tenant' ? activeMembership : null;
  const isElevatedWorkspaceAccess = activeScopeCode === 'tenant' && activeMembership === null;

  const contextValue: AuthenticationContextValue = {
    status,
    currentUser,
    platformPermissions,
    tenantPermissions,
    memberships,
    enabledModules,
    activeWorkspace,
    activeMembership,
    activeScopeCode,
    activeTenantId,
    isElevatedWorkspaceAccess,
    hasSessionExpiredNotice,
    acknowledgeSessionExpiredNotice,
    logInWithCredentials,
    completeSessionWithTokens,
    switchWorkspace,
    refreshCurrentUser,
    logOut,
  };

  return (
    <AuthenticationContext.Provider value={contextValue}>{children}</AuthenticationContext.Provider>
  );
}
